import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import API from '../../../../../services/API'
import {
  Alert,
  AlertTitle,
  Box,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { DetailRow } from '../../../../../components/Cards/DetailsBox'
import moment from 'moment'
import { Cancel, CheckCircle } from '@mui/icons-material'

const CheckComponent = ({ label, value }) => {
  const isBoolean = typeof value === 'boolean'
  const color = isBoolean
    ? value
      ? 'success.main'
      : 'error.main'
    : 'text.primary'

  return (
    <Grid item xs={12} sm={6} md={4} lg={2.4}>
      <Box
        sx={{
          borderRadius: 2,
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={500}
          mb={1}
        >
          {label}
        </Typography>

        <Box
          flexGrow={1}
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius={2}
          bgcolor="#f4f6f8"
          py={2}
        >
          {isBoolean ? (
            value ? (
              <CheckCircle sx={{ color, fontSize: 32 }} />
            ) : (
              <Cancel sx={{ color, fontSize: 32 }} />
            )
          ) : (
            <Typography
              variant="h5"
              fontWeight="bold"
              color={color}
              textAlign="center"
            >
              {value ?? '-'}
            </Typography>
          )}
        </Box>
      </Box>
    </Grid>
  )
}

const ViewCDAudit = () => {
  const { auditID } = useParams()
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [audit, setAudit] = useState(null)

  useEffect(() => {
    API.get('medsmx/employee-cd-audit/' + auditID)
      .then((res) => {
        if (res.status === 200) {
          setAudit(res.data)
          console.log(res.data)
        } else {
          setFetchError(true)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [auditID])

  return (
    <Stack component={Paper} padding={1} gap={2}>
      <Typography variant="title2">Controlled Drug Audit</Typography>
      {loading ? (
        <LinearProgress />
      ) : fetchError ? (
        <Alert severity="error">
          <AlertTitle>Unable to fetch Audit</AlertTitle>Please try again later.
        </Alert>
      ) : (
        <>
          <Typography variant="title3">
            {audit?.employee?.userAccount?.firstName}{' '}
            {audit?.employee?.userAccount?.lastName} (
            {audit?.employee?.clinicalRole})
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12} lg={6}>
              <DetailRow
                label="Date"
                value={moment(audit?.auditDate).format('Do MMM YYYY')}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <DetailRow label="Medicine" value={audit?.medicineName} />
            </Grid>
            <Grid item xs={12} lg={6}>
              <DetailRow
                label="Stock Source"
                value={
                  audit?.stockSource === 'ambunet'
                    ? 'AmbuNet-tracked stock'
                    : 'Personal CDs (held outside AmbuNet)'
                }
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <DetailRow
                label="Completed By"
                value={
                  audit?.completedBy?.firstName +
                  ' ' +
                  audit?.completedBy?.lastName
                }
              />
            </Grid>
            <Grid item xs={12} lg={6} marginBottom={2}>
              <DetailRow
                label="Witnessed By"
                value={
                  !audit.witnessedBy
                    ? ''
                    : audit?.witnessedBy?.firstName +
                      ' ' +
                      audit?.witnessedBy?.lastName
                }
              />
            </Grid>
            <CheckComponent label="Expected" value={audit?.expectedQuantity} />
            <CheckComponent label="Actual" value={audit?.actualQuantity} />
            <CheckComponent label="Match" value={audit?.qtyMatch} />
            <CheckComponent label="In Date" value={audit?.allInDate} />
            <CheckComponent label="Condition" value={audit?.inGoodCondition} />
            {audit?.stockSource === 'ambunet' && (
              <Grid item xs={12} marginTop={2}>
                <DetailRow
                  label="Calculated From Live Stock"
                  value={[
                    `${audit?.calculatedQuantity ?? '-'} total`,
                    ...(audit?.locationsCounted ?? []).map(
                      (row) => `${row.name}: ${row.qty}`,
                    ),
                    ...(audit?.preparedCounted
                      ? [`Prepared syringes: ${audit.preparedCounted}`]
                      : []),
                  ].join(' · ')}
                />
              </Grid>
            )}
            {audit?.overrideReason && (
              <Grid item xs={12}>
                <DetailRow
                  label="Expected Quantity Adjusted"
                  value={audit.overrideReason}
                />
              </Grid>
            )}
            {audit?.notes && (
              <Grid item xs={12} marginTop={2}>
                <DetailRow label="Notes" value={audit.notes} />
              </Grid>
            )}
          </Grid>
        </>
      )}
    </Stack>
  )
}

export default ViewCDAudit
